import { getDemoCompany, isDemoCastOrg } from '@/types/demoCompanies'
import type { TsEmployee } from '../types/timesheet.types'

/**
 * Scope a roster to the demo persona cast for the active company — the single
 * source of truth for which personas the switcher / Settings picker surface.
 *
 * - **Cast members** are those whose `primaryOrganizationId` is a cast root org
 *   (DB-driven via `isDemoCastOrg`). The per-company role-cast migrations
 *   (Gentex 00364, ConstructCo 00365, Boeing 00366) tag exactly these; the
 *   broader org roster carries a null `primary_organization_id`, so this cleanly
 *   isolates the cast with no hardcoded pernr allowlist.
 * - **Company scope:** when a company is selected (and it has a `castOrgId`),
 *   narrow to that company's cast so "pick a company → see its 9-role cast" holds.
 * - **Mock-mode fallback:** local dev (`VITE_DATA_SOURCE=mock`) loads the mock
 *   roster, which predates the canonical key and carries no cast org ids. When
 *   no persona has a cast org, return the roster unchanged so persona switching
 *   still works in dev.
 */
export function scopeDemoCast(
  all: TsEmployee[],
  companyId: string | null | undefined,
): TsEmployee[] {
  const cast = all.filter((e) => isDemoCastOrg(e.primaryOrganizationId))
  if (cast.length === 0) return all // mock-mode fallback
  const company = getDemoCompany(companyId)
  if (company?.castOrgId) {
    return cast.filter((e) => e.primaryOrganizationId === company.castOrgId)
  }
  return cast
}
